import { AVAILABLE_REGIONS, DEFAULT_REGION } from 'constants/regions';
import { curry, flowRight as compose } from 'lodash';

export const COOPERATIVE_KEY = 'cooperative';
export const REGION_SLUG_KEY = 'region_slug';

/**
 * Util function in order to be sure that the we can parse safely objects arguments or just return them raw as they are
 * @param {object|string} source - argument to parse
 * @param {string} key - key that we will eventually use to get the nested value we are looking for
 * @returns {string} hopefully a string we are trying to get
 * @example
 * safeParse({ cooperative: 'gmzh'} , 'cooperative') => 'gmzh'
 *
 * @example
 * safeParse('gmzh' , 'cooperative') => 'gmzh'
 */
function safeParse(source, key) {
    return source && typeof source === 'object' ? source[key] : source;
}

/**
 * Get the region slug by specific data structures
 * @param {function} regionRetriverFn - function we want to call to find the current region
 * @param {string} prop - property we want to use use as test value
 * @param {object} data - it could be either the user region model or a route node
 * @returns {string}
 */
export function getRegionSlug(regionRetriverFn, prop, data) {
    return regionRetriverFn(safeParse(data, prop))[REGION_SLUG_KEY];
}

// simple wrapper function to return the default region
export const findRegion = (prop, testValue) => AVAILABLE_REGIONS.find(r => r[prop] === testValue) || DEFAULT_REGION;

/**
 * Find a region by comparing the cooperative key with a test value
 * @param {string} cooperative - possible region cooperative
 * @example
 * findRegionBySlug('gmzh') => { region_slug: 'zuerich', cooperative: 'gmzh' }
 */
export const findRegionByCooperative = curry(findRegion)(COOPERATIVE_KEY);
/**
 * find a region by comparing the region_slug key with a test value
 * @param {string} slug - possible region slug
 * @example
 * findRegionBySlug('zuerich') => { region_slug: 'zuerich', cooperative: 'gmzh' }
 */
export const findRegionBySlug = curry(findRegion)(REGION_SLUG_KEY);

/**
 * Get the region by parsing an object having the `cooperative` key defined, if the region was not available it returns the default slug
 * @param {string|object} cooperative - either directly the cooperative string or an object containing the `cooperative` key
 * @returns {string} - slug of the region found or the default one
 *
 * @example
 * getRegionSlugByCooperative({ cooperative: 'gmzh' }) => 'zuerich';
 *
 * @example
 * getRegionSlugByCooperative('gmaa') => 'bern';
 *
 * @example
 * getRegionSlugByCooperative({ cooperative: 'foo' }) => 'zuerich'; // not available
 */
export const getRegionSlugByCooperative = curry(getRegionSlug)(findRegionByCooperative, COOPERATIVE_KEY);

/**
 * Get the region by parsing an object having the `region_slug` defined, if the region was not available it returns the default slug
 * @param {string|object} slug - either directly the region_slug string or an object containing the `region_slug` key
 * @returns {string} - slug of the region found or the default one
 *
 * @example
 * getRegionSlugBySlug({ region_slug: 'zuerich' }) => 'zuerich';
 *
 * @example
 * getRegionSlugBySlug('bern') => 'bern';
 *
 * @example
 * getRegionSlugBySlug('milano') => 'zuerich'; // not available
 */
export const getRegionSlugBySlug = curry(getRegionSlug)(findRegionBySlug, REGION_SLUG_KEY);

/**
 * Get the cooperative value of any region
 * @param {string} slug - test string to find the region cooperative
 * @returns {string} the region cooperative by the slug received
 */
const slugToCooperative = slug => findRegionBySlug(slug).cooperative;

/**
 * Compositional methods to get any region cooperative value
 * @param {string|object} slug - either directly the region_slug string or an object containing the `region_slug` key
 * @returns {string} - cooperative of the region found or the default one
 */
export const getCooperativeBySlug = compose(slugToCooperative, getRegionSlugBySlug);

/**
 * Compositional methods to get any region cooperative value
 * @param {string|object} cooperative - either directly the cooperative string or an object containing the `cooperative` key
 * @returns {string} - cooperative of the region found or the default one
 */
export const getCooperativeByCooperative = compose(slugToCooperative, getRegionSlugByCooperative);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/util/misc/get-region.js